import { lightFormat, parseISO } from 'date-fns';

import { Properties, Reservations } from '@lib/state';
import { RoomCountType } from '@lib/state/api/generated/properties';

/**
 * One column of the tape chart's room counts. For every date after the property's hotel day:
 *
 *   occupiedRooms - checkOutCount + checkInCount = the next date's occupiedRooms
 *   availableRooms = totalRooms - occupiedRooms - checkInCount + checkOutCount + adjustments
 *
 * The figures combine the Property API (checked-in guests, whole rooms or beds) with the Reservation
 * API (open reservations, half a room per double guest in Rooms view). Each day's totals are rounded
 * once and Checking In / Checking Out are derived from the rounded totals, so unarrived double guests
 * are assumed to share a room two at a time even when they arrive or leave on different days.
 */
export interface RoomCount {
  id: string;
  propertyId: string;

  date: Date;

  /**
   * In Service minus the rooms needed tonight, plus inventory adjustments. Negative when oversold.
   */
  availableRooms: number;

  /**
   * Rooms needed last night (checked-in guests plus open reservations), rounded up
   */
  occupiedRooms: number;

  /**
   * Rooms needed tonight by open reservations, unrounded (half a room per double guest in Rooms view)
   */
  reservedRooms: number;

  /**
   * Rooms that start being needed tonight: rounded tonight minus rounded rooms needed both nights
   */
  checkInCount: number;

  /**
   * Rooms that stop being needed tonight: rounded last night minus rounded rooms needed both nights
   */
  checkOutCount: number;

  /**
   * Rooms in service
   */
  totalRooms: number;

  /**
   * Clean rooms
   */
  readyRooms: number;

  /**
   * Inventory adjustments
   */
  adjustments: number;

  /**
   * The request these figures answer (property, dates, view, room-type filter). The chart renders
   * only counts whose key matches its current settings, so a previous request's figures never show
   * under a new setting, not even for the frame before the store is cleared.
   */
  requestKey: string;

  /**
   * Today's column keeps its own meaning: Occupied is everyone in house including rooms leaving today;
   * Checking In is every reservation still in Open status that could arrive today, whether its check-in
   * date is today or already passed (once it is marked No-Show, Canceled or checks in it leaves this
   * figure); Checking Out is the rooms leaving today. The Property API reports no check-ins for today,
   * so these still reconcile with tomorrow's Occupied.
   */
  today: {
    occupiedRooms: number;
    checkInCount: number;
    checkOutCount: number;
  };
}

/**
 * Rounds a room total up to a whole room. Totals are sums of 1 / occupancy shares, so a value within
 * 1e-6 of a whole number is that whole number (three one-third shares must round to 1, not 2).
 */
export function ceilRooms(value: number): number {
  const nearest = Math.round(value);
  const rounded = Math.abs(value - nearest) < 1e-6 ? nearest : Math.ceil(value);
  return rounded === 0 ? 0 : rounded; // never -0
}

/**
 * Identifies one room-count request. The service tags every count it stores with this key and the
 * tape chart hook shows only counts whose key matches its current settings.
 */
export function roomCountRequestKey(
  propertyId: string,
  startDate: Date,
  endDate: Date,
  countBy: RoomCountType,
  roomTypeId?: string
): string {
  return [
    propertyId,
    lightFormat(startDate, 'yyyy-MM-dd'),
    lightFormat(endDate, 'yyyy-MM-dd'),
    countBy,
    roomTypeId ?? '',
  ].join('|');
}

export function mapRoomCount(
  propertyId: string,
  property: Properties.RoomCountModel,
  reservation: Reservations.RoomCountModel,
  requestKey = ''
): RoomCount {
  const checkIn = property.checkInCount + reservation.checkInCount;
  const checkOut = property.checkOutCount + reservation.checkOutCount;

  // rooms needed tonight, on both nights, and last night; fractional for doubles
  const tonight = property.occupiedRooms + reservation.reservedRooms;
  const stayThrough = tonight - checkIn;
  const lastNight = stayThrough + checkOut;

  // round each total once, then derive the flows from the rounded totals so that
  // occupied - checkOut + checkIn equals the rooms needed tonight, which is tomorrow's occupied
  const tonightRooms = ceilRooms(tonight);
  const stayThroughRooms = ceilRooms(stayThrough);
  const lastNightRooms = ceilRooms(lastNight);

  const checkOutToday = ceilRooms(checkOut);

  return {
    id: property.date,
    propertyId,
    date: parseISO(property.date),
    availableRooms: reservation.totalRooms - tonightRooms + reservation.inventoryAdjustments,
    occupiedRooms: lastNightRooms,
    checkInCount: tonightRooms - stayThroughRooms,
    checkOutCount: lastNightRooms - stayThroughRooms,
    reservedRooms: reservation.reservedRooms,
    totalRooms: reservation.totalRooms,
    readyRooms: reservation.readyRooms,
    adjustments: reservation.inventoryAdjustments,
    requestKey,

    today: {
      occupiedRooms: property.occupiedRooms + checkOutToday,
      // every reservation still in Open status that could check in today: those due today plus those
      // past their check-in date and not yet marked No-Show; fractional counts are summed first and
      // rounded once
      checkInCount: ceilRooms(reservation.checkInCount + reservation.openReservations),
      checkOutCount: checkOutToday,
    },
  };
}
